import AppBar from '@mui/material/AppBar';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {observer} from 'mobx-react-lite';
import React, {useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {
  SUB_NAV_ITEM_CALENDAR,
  SUB_NAV_ITEM_CREATE_PATIENT,
  SUB_NAV_ITEM_DO,
  SUB_NAV_ITEM_MIN_MEMORIA,
  SUB_NAV_ITEM_MY_PATIENTS,
  SUB_NAV_ITEM_OP_INBOX,
  SUB_NAV_ITEM_SUPPORT,
} from '../../configs/sub-nav-items';
import {HeaderContainer} from '../../containers/header.container';
import {usePatients} from '../../hooks/patients/use-patients';
import {useAllUnits} from '../../hooks/units/use-all-units';
import {useStores} from '../../hooks/use-stores';
import {Unit} from '../../interfaces/entities/unit.interface';
import {BASE_PRODUCTS, getProductUrl} from '../../interfaces/products';
import {PRODUCTS} from '../../interfaces/products/products.enum';
import {SubNavItem} from '../../interfaces/sub-nav-item.interface';
import {DEPARTMENT_UNIT_TYPE} from '../../stores/units.store';
import {LOCALES} from '../../utils/constants';
import {hasAccessToProduct} from '../../utils/has-access-to-product';
import {isUserAdmin} from '../../utils/is-user-admin';
import {mapVilmerLanguage} from '../../utils/map-vilmer-language';
import {DesktopNavigationComponent} from './desktop-navigation.component';
import {MobileNavigationComponent} from './mobile-navigation/mobile-navigation.component';

function Navigation() {
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down('md'));
  const {i18n} = useTranslation();

  const {authStore, usersStore} = useStores();

  const {items, loading, patientsWithNewGuestbookPosts} = usePatients();
  const {items: units = [], loading: loadingUnits} = useAllUnits();

  useEffect(() => {
    authStore.fetchMe();
  }, [authStore.fetchMe]);

  const departments: Unit[] = useMemo(() => {
    const n = units.length;
    if (loadingUnits !== false || !n) {
      return [];
    }

    const arr: Unit[] = [];
    for (let i = 0; i < n; i++) {
      if (units[i].type === DEPARTMENT_UNIT_TYPE) {
        arr.push(units[i]);
      }
    }

    return arr;
  }, [loadingUnits, units]);

  const isAdmin = useMemo(() => {
    return authStore.me && isUserAdmin(authStore.me);
  }, [authStore.me]);

  const hasOPUnit = useMemo(() => {
    return hasAccessToProduct({userUnits: usersStore?.userUnits, productId: BASE_PRODUCTS.Opplevelser.id});
  }, [usersStore?.userUnits]);

  const hasDOUnit = useMemo(() => {
    return hasAccessToProduct({userUnits: usersStore?.userUnits, productId: BASE_PRODUCTS.DigitaleOpplevelser.id});
  }, [usersStore?.userUnits]);

  const subNavItems = useMemo(() => {
    const arr: SubNavItem[] = [SUB_NAV_ITEM_MIN_MEMORIA, SUB_NAV_ITEM_SUPPORT];

    if (hasDOUnit) {
      arr.unshift(SUB_NAV_ITEM_DO);
    }

    if (hasOPUnit) {
      const inboxUrl = new URL(SUB_NAV_ITEM_OP_INBOX.href || getProductUrl(PRODUCTS.Dashboard));
      inboxUrl.pathname = `/${mapVilmerLanguage(i18n.language as LOCALES)}/chats`;
      SUB_NAV_ITEM_OP_INBOX.href = inboxUrl.toString();

      arr.unshift(SUB_NAV_ITEM_OP_INBOX);

      const calendarUrl = new URL(SUB_NAV_ITEM_CALENDAR.href || getProductUrl(PRODUCTS.Dashboard));
      calendarUrl.pathname = `/${mapVilmerLanguage(i18n.language as LOCALES)}`;
      SUB_NAV_ITEM_CALENDAR.href = calendarUrl.toString();

      arr.unshift(SUB_NAV_ITEM_CALENDAR);
    }

    if (departments.length > 0 && mobileView) {
      arr.unshift(SUB_NAV_ITEM_CREATE_PATIENT);
    }

    if (loading == false && (items.length > 1 || isAdmin) && mobileView) {
      arr.unshift({
        ...SUB_NAV_ITEM_MY_PATIENTS,
        counter: patientsWithNewGuestbookPosts,
      });
    }

    return arr;
  }, [
    departments.length,
    mobileView,
    loading,
    items.length,
    isAdmin,
    hasDOUnit,
    hasOPUnit,
    patientsWithNewGuestbookPosts,
    i18n.language,
  ]);

  return (
    <AppBar
      color={'inherit'}
      elevation={0}
      position={'relative'}
      sx={theme => ({
        zIndex: theme.zIndex.appBar + (mobileView ? 1 : 150),
        position: 'fixed',
      })}
    >
      <HeaderContainer open />
      <DesktopNavigationComponent open={!mobileView} items={subNavItems} />
      <MobileNavigationComponent open={mobileView} items={subNavItems} logged={!!authStore.me} />
    </AppBar>
  );
}

export const NavigationComponent = observer(Navigation);
